import { OPERATION_ERROR_CODES, type OperationErrorCode } from "@malfak/contracts";
import { toast } from "sonner";
import { ui } from "@/app/ui-store";
import { toastPlanProblem } from "@/features/plans/messages";
import { isApiError } from "@/lib/api/errors";
import { i18n } from "@/lib/i18n";

/**
 * "The channel token is no longer valid" (ALP-77): the API answers a 409 problem with title `channel_reauth_required`
 * when an action targets an account that must be reconnected, and the worker finishes operations / jobs / approval
 * executions with `errorCode: "channel_reauth_required"`. Everywhere the UI would say "failed", it says
 * "Instagram needs to be reconnected" and offers Reconnect instead.
 */
export const REAUTH_CODE = "channel_reauth_required";

/** True for an ApiError / problem / operation / job / approval execution / SSE event that failed because of reauth. */
export function isReauth(x: unknown): boolean {
  if (!x) return false;
  if (typeof x === "string") return x === REAUTH_CODE;
  if (isApiError(x)) return x.problem?.title === REAUTH_CODE || !!x.problem?.type?.endsWith(`/${REAUTH_CODE}`);
  if (typeof x !== "object") return false;
  const o = x as { errorCode?: unknown; code?: unknown; title?: unknown; type?: unknown; execution?: unknown };
  if (o.errorCode === REAUTH_CODE || o.code === REAUTH_CODE || o.title === REAUTH_CODE) return true;
  if (typeof o.type === "string" && o.type.endsWith(`/${REAUTH_CODE}`)) return true;
  return o.execution ? isReauth(o.execution) : false;
}

/** Open the Instagram connect explainer from anywhere (toast actions); the app shell hosts the dialog. */
export function openReconnect() {
  ui.set({ reconnect: true });
}

/** The reauth toast: clear message + a Reconnect action (one per id, so repeated failures don't stack). */
export function reauthToast(opts: { id?: string | number; username?: string | null } = {}) {
  toast.error(i18n.t("oauth.reauth.needed"), {
    id: opts.id ?? "reauth-needed",
    description: opts.username ? i18n.t("oauth.reauth.hintUser", { user: opts.username }) : i18n.t("oauth.reauth.hint"),
    duration: 12_000,
    action: { label: i18n.t("oauth.reauth.reconnect"), onClick: openReconnect },
  });
}

// ------------------------------------------------------------------ every send error → one message + action
/** Temporal's generic wrapper message on legacy rows (no code): never shown as detail. */
export const TEMPORAL_WRAPPER = "Activity task failed";
const KNOWN = new Set<string>(OPERATION_ERROR_CODES);
/** API problem titles / older codes that mean one of the stable codes. */
const ALIASES: Record<string, OperationErrorCode> = {
  validation_failed: "validation",
  channel_unavailable: "provider_error",
  internal: "provider_error",
  unauthenticated: "forbidden",
};
const RETRYABLE = new Set<OperationErrorCode>(["rate_limited", "provider_error", "unknown", "ai_unavailable", "storage_error", "timeout"]);

export type SendErrorAction = "reconnect" | "retry" | null;
export interface SendErrorInfo {
  code: OperationErrorCode;
  /** Localized one-liner ("Instagram needs to be reconnected"). */
  message: string;
  /** The raw reason as secondary text, unless it is Temporal's wrapper (or repeats the message). */
  detail: string | null;
  action: SendErrorAction;
}

/** Normalize anything that failed (job, operation event, message, approval execution, ApiError) to a stable code. */
export function sendErrorCode(x: { errorCode?: string | null; error?: string | null } | unknown): OperationErrorCode {
  let raw: string | null | undefined;
  if (isApiError(x)) raw = x.problem?.title;
  else if (x && typeof x === "object") raw = (x as { errorCode?: string | null }).errorCode;
  if (raw && KNOWN.has(raw)) return raw as OperationErrorCode;
  const alias = raw ? ALIASES[raw] : undefined;
  if (alias) return alias;
  return "unknown";
}

/** One localized message + the action to offer (Reconnect / Retry / none) for a failed send or job. */
export function sendErrorInfo(x: { errorCode?: string | null; error?: string | null } | unknown): SendErrorInfo {
  const code = sendErrorCode(x);
  const message = i18n.t(`common.sendError.codes.${code}`);
  const rawError = isApiError(x) ? (x.problem?.detail ?? null) : x && typeof x === "object" ? ((x as { error?: string | null }).error ?? null) : null;
  const text = rawError?.trim() ?? "";
  const detail = !text || text.startsWith(TEMPORAL_WRAPPER) || text === message ? null : text;
  return { code, message, detail, action: code === REAUTH_CODE ? "reconnect" : RETRYABLE.has(code) ? "retry" : null };
}

/** Readable text of an API failure (problem detail, else title). */
export function problemText(e: unknown): string | undefined {
  return isApiError(e) ? (e.problem?.detail ?? e.problem?.title) : undefined;
}

/** Where a login with no organization goes ("Create your organization"); set by the app (router) at start-up. */
let goCreateOrg: () => void = () => {
  if (typeof window !== "undefined") window.location.assign("/create-org");
};
export const openCreateOrg = () => goCreateOrg();
export const setCreateOrgNavigator = (fn: () => void) => {
  goCreateOrg = fn;
};

/** True when the call failed because the user has no current organization (client-side `no_organization`). */
export const isNoOrganization = (e: unknown) => isApiError(e) && e.isNoOrganization;

/** "You're not part of an organization yet" + a Create action (one toast, never stacked). */
export function noOrgToast() {
  toast.error(i18n.t("common.noOrg.title"), {
    id: "no-organization",
    description: i18n.t("common.noOrg.hint"),
    duration: 12_000,
    action: { label: i18n.t("common.noOrg.create"), onClick: () => goCreateOrg() },
  });
}

/** Error toast for a failed mutation: the reauth toast when that is the cause, else `title` + the problem detail. */
export function toastFailure(e: unknown, title: string) {
  if (isNoOrganization(e)) noOrgToast();
  else if (isReauth(e)) reauthToast();
  else if (toastPlanProblem(e)) return; // plans (ALP-81): budget / limit / feature messages
  else toast.error(title, { description: problemText(e) || undefined });
}
